import { action, intercept, makeObservable, observable } from 'mobx'
import { IValidationSchema, required, validationContext } from 'validx'
import { PhysicalAddress } from '@taxfyle/api-internal/shared/types/physical_address_pb'
import {
  maxLength,
  mustBeValidTaxFormInputValue,
  when,
} from 'utils/validation-utils'

export default class PhysicalAddressEditorViewModel {
  @observable addressLine1 = ''

  @observable addressLine2 = ''

  @observable city = ''

  @observable region = ''

  @observable postalCode = ''

  @observable country = 'US'

  private schema: IValidationSchema<this> = {
    addressLine1: [
      required('Address line 1 is required'),
      maxLength(100, 'Address line 1 must not exceed 100 characters'),
      mustBeValidTaxFormInputValue(),
    ],
    addressLine2: [
      maxLength(100, 'Address line 2 must not exceed 100 characters'),
      mustBeValidTaxFormInputValue(),
    ],
    city: [
      required('City is required'),
      mustBeValidTaxFormInputValue(),
      maxLength(100, 'City must not exceed 100 characters'),
    ],
    region: [
      when(
        () => this.country === 'US',
        required('State or territory is required'),
      ),
      mustBeValidTaxFormInputValue(),
      maxLength(100, 'Region must not exceed 100 characters'),
    ],
    postalCode: [
      required('Zip/postal code is required'),
      mustBeValidTaxFormInputValue(),
      maxLength(20, 'Zip/postal code must not exceed 20 characters'),
    ],
    country: [required('Country is required')],
  }

  validation = validationContext(this)

  constructor() {
    makeObservable(this)

    intercept(this, (change) => {
      this.validation.clearErrors(String(change.name))
      return change
    })
  }

  @action.bound
  validate() {
    return this.validation.reset().validate(this.schema).isValid
  }

  @action
  reset(address?: PhysicalAddress.AsObject) {
    this.addressLine1 = address?.addressLine1 ?? ''
    this.addressLine2 = address?.addressLine2 ?? ''
    this.city = address?.city ?? ''
    this.region = address?.region ?? ''
    this.postalCode = address?.postalCode ?? ''
    this.country = address?.country || 'US'
    this.validation.reset()
  }

  toPhysicalAddress(): PhysicalAddress.AsObject {
    return {
      addressLine1: this.addressLine1,
      addressLine2: this.addressLine2,
      city: this.city,
      region: this.region,
      postalCode: this.postalCode,
      country: this.country,
    }
  }
}
